import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { submitLead } from "@/lib/api";
import { toast } from "sonner";

export default function EbookModal({ open, onOpenChange, ebook }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    if (!ebook) return null;

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await submitLead({ email: email.trim(), name: name.trim() || null, source: "ebook", ebook_slug: ebook.slug });
            setDone(true);
            toast.success("Sent! Check your email for the download link.");
        } catch (err) {
            toast.error("Couldn't save your details. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const reset = () => {
        setDone(false);
        setName("");
        setEmail("");
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(v) : reset())}>
            <DialogContent className="max-w-md" data-testid="ebook-modal">
                <DialogHeader>
                    <p className="label-eyebrow text-accent">Free download</p>
                    <DialogTitle className="font-serif text-2xl leading-tight mt-1">{ebook.title}</DialogTitle>
                    <DialogDescription>{ebook.subtitle}</DialogDescription>
                </DialogHeader>

                {!done ? (
                    <form onSubmit={submit} className="space-y-3 mt-2">
                        <Input
                            data-testid="ebook-modal-name"
                            placeholder="First name (optional)"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="h-11"
                        />
                        <Input
                            data-testid="ebook-modal-email"
                            type="email"
                            required
                            placeholder="you@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="h-11"
                        />
                        <Button
                            data-testid="ebook-modal-submit"
                            type="submit"
                            disabled={loading}
                            className="w-full h-11 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
                        >
                            {loading ? "Preparing your download…" : "Send me the PDF"}
                        </Button>
                        <p className="text-[11px] text-stone-500 text-center">
                            We'll also add you to the Monday newsletter. Unsubscribe any time.
                        </p>
                    </form>
                ) : (
                    <div className="text-center py-6 space-y-3" data-testid="ebook-modal-success">
                        <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 grid place-items-center">
                            <Check className="h-6 w-6 text-primary" />
                        </div>
                        <p className="font-serif text-xl">You're in!</p>
                        <p className="text-sm text-stone-600">
                            Your copy of <span className="font-semibold">{ebook.title}</span> is ready.
                        </p>
                        {ebook.pdf ? (
                            <>
                                <a
                                    href={ebook.pdf}
                                    download
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="ebook-modal-download-btn"
                                    className="inline-flex items-center gap-2 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-11 px-6 text-sm font-semibold"
                                >
                                    <Check className="h-4 w-4" /> Download the PDF now
                                </a>
                                <p className="text-[11px] text-stone-500 pt-2">
                                    We've also added you to the Monday newsletter.
                                </p>
                            </>
                        ) : (
                            <a
                                href="#"
                                data-testid="ebook-modal-download-fallback"
                                className="text-sm underline text-primary"
                            >
                                Check your email for the download link
                            </a>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
